import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import {ChangeEvent, FC, ReactNode, useMemo} from "react";
import CustomTableHeader from "./custom-table-header/CustomTableHeader";
import {ITableHeader} from "./CustomTableHelper";
import TableLoader from "./table-loader/TableLoader";
import EmptyRow from "./empty-row/EmptyRow";
import {useTranslation} from "react-i18next";
import {alpha, createTheme, ThemeProvider} from "@mui/material";
import {enUS, mkMK} from "@mui/material/locale";
import Box from "@mui/material/Box";
import ScrollFader from "../scroll-fader/ScrollFader";

interface Props {
    headCells: ITableHeader[]
    tableBody: ReactNode[]
    page?: number
    setPage?: (page: number) => void
    limit?: number
    setLimit?: (limit: number) => void
    count: number
    loading: boolean
    noPagination?: boolean
    transparent?: boolean
}

const CustomTable: FC<Props> = (props) => {
    const {i18n} = useTranslation()

    const theme = useMemo(() => {
        return createTheme({
            ...(i18n.language === 'mk' ? mkMK : enUS)
        });
    }, [i18n.language])

    const handleChangePage = (event: unknown, newPage: number) => {
        props.setPage!(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        props.setLimit!(parseInt(event.target.value, 10));
        props.setPage!(0);
    };

    return (
        <Paper sx={{
            ...(props.transparent ? {backgroundColor: 'transparent'} : {}),
            width: '100%',
            overflow: 'hidden',
        }}>
            <Box sx={{position: 'relative'}}>
                <TableContainer>
                    <ScrollFader direction="horizontal" faderSize={60}
                                 color={props.transparent ? 'secondary' : 'primary'}>
                    <Table>
                        <CustomTableHeader
                            headCells={props.headCells}/>
                        <TableBody>
                            {props.loading ? <TableLoader colSpan={props.headCells.length}
                                                          color={props.transparent ? 'primary' : 'secondary'}/> :
                                !props.count ? <EmptyRow colSpan={props.headCells.length}/> :
                                    props.tableBody}
                        </TableBody>
                    </Table>
                    </ScrollFader>
                </TableContainer>
            </Box>
            {props.loading || !props.count || props.noPagination ? null :
                <ThemeProvider theme={theme}>
                    <TablePagination
                        sx={props.transparent ? {
                            backgroundColor: alpha(theme.palette.common.white, .05),
                            color: theme.palette.common.white,
                            "& .MuiTablePagination-selectIcon": {
                                color: theme.palette.common.white
                            },
                            "& .Mui-disabled": {
                                color: "#9e9e9e !important"
                            }
                        } : {}}
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={props.count}
                        rowsPerPage={props.limit!}
                        page={props.page!}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </ThemeProvider>}
        </Paper>
    );
}

export default CustomTable
